import CloseOutlined from '@ant-design/icons/lib/icons/CloseOutlined';
import { ArchiveBoxXMarkIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { default as Collapse } from 'antd/es/collapse';
import type { CollapseProps } from 'antd/es/collapse';
import { default as Modal } from 'antd/es/modal';
import { default as Skeleton } from 'antd/es/skeleton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch } from 'react';
import { useForm } from 'react-hook-form';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { formatDecimalAmount } from 'utils/formatters/formatCurrency';
import { hasValue } from 'utils/hasValue';
import { isSandboxEnvironment } from 'utils/sandbox/isSandboxEnvironment';

import { SandboxOrderDetailsTestCaseSection } from '@sandbox/SandboxOrderDetailsTestCaseSection';
import {
  useDeleteCollectionOrdersUuid,
  useGetCollectionOrdersUuid,
  usePostCollectionOrdersUuidRefund
} from 'api';
import type {
  CollectionOrderPaymentItem,
  CollectionStatusHistoryItem,
  ErrorResponse
} from 'api';
import { Restricted } from 'containers/Restricted';
import { getPartialRefundSchema } from 'validators/collectionOrderPartialRefundDialog';

import { COLLECTION_ORDER_STATUS_ENUM } from 'models/order';

import { useCurrentOrganisation, useOrganisationId } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';

import { OrderBadge } from '@atoms/badges';
import { Badge } from '@atoms/badges/Badge/Badge';
import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { TertiaryButton } from '@atoms/buttons/TertiaryButton';
import { DetailsItem } from '@atoms/DetailsItem/DetailsItem';

import { AmountInput } from '@molecules/AmountInput';
import { CopyToClipboardButton } from '@molecules/CopyToClipboardButton';
import { Country } from '@molecules/Country';
import { FormattedDateTime } from '@molecules/FormattedDateTime';
import { LabelledInput } from '@molecules/LabelledInput';
import { OrderPaymentEntry } from '@molecules/OrderPaymentEntry';

import {
  COLLECTIONS_CANCEL_BUTTON_TEST_ID,
  COLLECTIONS_FULL_REFUND_BUTTON_TEST_ID,
  COLLECTIONS_PARTIAL_REFUND_AMOUNT_INPUT_TEST_ID,
  COLLECTIONS_PARTIAL_REFUND_OPEN_MODAL_BUTTON_TEST_ID,
  COLLECTIONS_PARTIAL_REFUND_SUBMIT_BUTTON_TEST_ID,
  TRANSLATIONS
} from './constants';
import {
  formatErrors,
  showErrorNotification,
  showSuccessNotification
} from './util';

import { COLLECTION_ORDERS_DETAILS } from '../constants';

type ORDERS_KNOWN_SECTIONS =
  | 'summary'
  | 'order'
  | 'debtor'
  | 'payments'
  | 'history'
  | 'sandbox';

type CollapseItemType = NonNullable<CollapseProps['items']>[number];

interface CollapseItemWithTypedSection extends Omit<CollapseItemType, 'key'> {
  key?: ORDERS_KNOWN_SECTIONS;
}

const SUMMARY_STATUSES = [
  'cancelled',
  'action_required_for_refund',
  'action_required',
  'refund_returned'
];

type RefundForm = {
  amount: string;
};

type Props = {
  onClose: VoidFunction;
  orderId: string;
  onCancel?: Dispatch<void>;
  onRefundSuccess?: Dispatch<void>;
  /**
   * Called when the order is completed. Sandbox environment only.
   */
  onTestCaseSuccess?: VoidFunction;
};

export const OrderDetails = ({
  orderId,
  onClose,
  onCancel = () => {},
  onRefundSuccess = () => {},
  onTestCaseSuccess
}: Props) => {
  const [orderRefunded, setOrderRefunded] = useState<boolean>(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);
  const [isPartialRefundDialogOpen, setIsPartialRefundDialogOpen] =
    useState<boolean>(false);
  const [isFullRefundDialogOpen, setIsFullRefundDialogOpen] =
    useState<boolean>(false);

  const organisationId = useOrganisationId<number>();
  const openNotification = useOpenNotification();
  const organisation = useCurrentOrganisation();
  const { isLoading: isFeatureAccessLoading, isSandboxTestCasesEnabled } =
    useFeatureAccessControl();

  const { mutateAsync: refundOrderMutation } =
    usePostCollectionOrdersUuidRefund();

  const {
    data: { data: orderData } = {},
    isLoading,
    isError,
    isSuccess
  } = useGetCollectionOrdersUuid(organisationId, orderId);

  // Partial Refund
  const {
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    clearErrors
  } = useForm<RefundForm>({
    defaultValues: {
      amount: undefined
    },
    resolver:
      isSuccess && orderData?.amount && orderData?.currency_code
        ? yupResolver(
            getPartialRefundSchema(
              parseFloat(orderData.amount),
              orderData.currency_code
            )
          )
        : undefined
  });

  const closeRefundDialogs = useCallback(() => {
    reset();
    setIsPartialRefundDialogOpen(false);
    setIsFullRefundDialogOpen(false);
  }, [reset, setIsPartialRefundDialogOpen, setIsFullRefundDialogOpen]);

  const onRefundSubmit = useCallback(
    (refundForm: RefundForm) => {
      refundOrderMutation({
        organisationId: organisationId,
        uuid: orderData!.uuid,
        data: { amount: refundForm.amount }
      })
        .then(() => {
          openNotification({
            kind: 'success',
            message: TRANSLATIONS.PARTIAL_REFUND_SUCCESS_NOTIFICATION,
            key: 'refund-succeeded'
          });
          setOrderRefunded(true);
          onRefundSuccess();
        })
        .catch((error) => {
          const details = error?.error?.details;

          if (details === TRANSLATIONS.PARTIAL_REFUND_422_NO_REFUND_FEE) {
            openNotification({
              kind: 'error',
              message: 'Missing configuration',
              description: 'Please contact support',
              key: 'refund-failed-no-refund-fee'
            });
          } else {
            openNotification({
              kind: 'error',
              message: details,
              key: 'refund-failed'
            });
          }
        })
        .finally(() => closeRefundDialogs());
    },
    [
      closeRefundDialogs,
      onRefundSuccess,
      openNotification,
      orderData,
      organisationId,
      refundOrderMutation
    ]
  );

  // Cancel Order
  const { mutateAsync: cancelOrderMutation } = useDeleteCollectionOrdersUuid();
  const cancelOrder = useCallback(
    async (cancelOrderDetails: Parameters<typeof cancelOrderMutation>[0]) => {
      try {
        await cancelOrderMutation(cancelOrderDetails);
        onCancel();
        showSuccessNotification(openNotification, orderId);
      } catch (error: unknown) {
        const errorMsg = error
          ? formatErrors(error as ErrorResponse)
          : 'An error occurred';
        showErrorNotification(openNotification, errorMsg);
      } finally {
        setIsCancelDialogOpen(false);
      }
    },
    [cancelOrderMutation, onCancel, openNotification, orderId]
  );

  const hasReasonCodes = useMemo(() => {
    return hasValue(orderData) && orderData.reason_codes.length > 0;
  }, [orderData]);

  const hasOrderSummary = useMemo(() => {
    return (
      hasValue(orderData) &&
      Object.values(SUMMARY_STATUSES).includes(orderData.status)
    );
  }, [orderData]);

  const [activeSection, setActiveSection] =
    useState<ORDERS_KNOWN_SECTIONS>('order');

  useEffect(() => {
    setActiveSection(hasReasonCodes && hasOrderSummary ? 'summary' : 'order');
  }, [hasOrderSummary, hasReasonCodes]);

  const collapseItems: CollapseItemWithTypedSection[] = useMemo(() => {
    if (!hasValue(orderData) || !isSuccess || isFeatureAccessLoading) {
      return [];
    }

    const sections: CollapseItemWithTypedSection[] = [];

    if (
      isSandboxEnvironment() &&
      isSandboxTestCasesEnabled &&
      onTestCaseSuccess
    ) {
      sections.push({
        key: 'sandbox',
        label: 'Sandbox',
        children: (
          <SandboxOrderDetailsTestCaseSection
            order={orderData}
            onSuccess={onTestCaseSuccess}
          />
        )
      });
    }

    if (hasOrderSummary) {
      sections.push({
        key: 'summary',
        label: 'Summary',
        children: (
          <div className="pb-6">
            <div className="mb-4">
              {hasReasonCodes && (
                <div className="flex flex-wrap gap-2">
                  {orderData.reason_codes.map((reasonCodeItem) => {
                    return (
                      <Badge
                        label={reasonCodeItem.reason}
                        className="cursor-pointer bg-feedback-error-surface"
                        key={reasonCodeItem.symbol}
                      />
                    );
                  })}
                </div>
              )}
              {!hasReasonCodes && (
                <Badge
                  key="unknown"
                  label="Unknown"
                  className="bg-feedback-error-surface"
                />
              )}
            </div>
            {orderData.reason_codes.map((reasonCodeItem) => {
              return (
                <div key={reasonCodeItem.symbol}>
                  {reasonCodeItem.external_message.replace(
                    '###merchant_name###',
                    organisation.name
                  )}
                </div>
              );
            })}
          </div>
        )
      });
    }
    sections.push(
      {
        key: 'order',
        label: 'Order',
        children: (
          <div className="my-4 flex flex-col gap-4">
            <div data-testid="order-details-business-unit">
              <p className="text-s break-all text-content-secondary">
                Business Unit:
              </p>

              <h4 className="text-base font-medium">
                {orderData?.business_unit}
              </h4>
            </div>
            <div data-testid="order-details-reference">
              <p className="text-s break-all text-content-secondary">
                Reference:
              </p>

              <h4 className="text-base font-medium">
                <CopyToClipboardButton
                  text={orderData?.reference?.toLocaleUpperCase()}
                />
              </h4>
            </div>
            <div data-testid="order-details-e2e-id">
              <p className="text-s break-all text-content-secondary">
                End-to-end ID:
              </p>

              <h4 className="text-base font-medium">
                <CopyToClipboardButton
                  text={orderData?.uuid?.toLocaleUpperCase()}
                />
              </h4>
            </div>
            {orderData?.refunds?.[0] && (
              <div data-testid="order-details-e2e-id">
                <p className="text-s break-all text-content-secondary">
                  Refunded amount:
                </p>

                <h4 className="text-base font-medium">
                  {formatDecimalAmount(Number(orderData?.refunds?.[0]?.amount))}{' '}
                  {orderData?.refunds?.[0]?.currency_iso}
                </h4>
              </div>
            )}
            {(orderData?.status === COLLECTION_ORDER_STATUS_ENUM.PENDING ||
              orderData?.status === COLLECTION_ORDER_STATUS_ENUM.RECEIVED) && (
              <div className="items-center gap-2 space-y-4">
                <Restricted requiredPermission="collection_orders:manage">
                  <hr />
                  <SecondaryButton
                    data-testid={COLLECTIONS_CANCEL_BUTTON_TEST_ID}
                    htmlType="submit"
                    danger
                    className="flex w-full items-center justify-center gap-1"
                    onClick={() => {
                      setIsCancelDialogOpen(true);
                    }}
                  >
                    <ArchiveBoxXMarkIcon className="size-5" />
                    {TRANSLATIONS.CANCEL_ORDER_BUTTON}
                  </SecondaryButton>
                </Restricted>
              </div>
            )}
            {!orderRefunded &&
              orderData?.status === COLLECTION_ORDER_STATUS_ENUM.COMPLETED && (
                <div className="flex items-center gap-2">
                  <Restricted requiredPermission="collection_orders:manage">
                    <PrimaryButton
                      data-testid={
                        COLLECTIONS_PARTIAL_REFUND_OPEN_MODAL_BUTTON_TEST_ID
                      }
                      htmlType="submit"
                      size="large"
                      danger
                      onClick={() => {
                        setIsPartialRefundDialogOpen(true);
                      }}
                    >
                      {TRANSLATIONS.PARTIAL_REFUND}
                    </PrimaryButton>
                    <PrimaryButton
                      data-testid={COLLECTIONS_FULL_REFUND_BUTTON_TEST_ID}
                      htmlType="submit"
                      size="large"
                      danger
                      onClick={() => setIsFullRefundDialogOpen(true)}
                    >
                      {TRANSLATIONS.FULL_REFUND}
                    </PrimaryButton>
                  </Restricted>
                </div>
              )}
          </div>
        )
      },
      {
        key: 'debtor',
        label: 'Debtor',
        children: (
          <div>
            <DetailsItem
              title="Name"
              item={orderData.debtor.full_name}
              data-testid="order-details-debtor-name"
            />
            <DetailsItem
              title="Date of birth"
              item={orderData.debtor.date_of_birth}
              data-testid="order-details-debtor-dob"
            />
            <DetailsItem
              title="Email"
              item={orderData.debtor.email}
              data-testid="order-details-debtor-email"
            />
            <DetailsItem
              title="Country of Residence"
              item={
                <Country countryIso={orderData.debtor.country_of_residence} />
              }
            />
          </div>
        )
      },
      {
        key: 'payments',
        label: 'Payments',
        children: orderData.payments?.length ? (
          orderData.payments.map((payment: CollectionOrderPaymentItem) => (
            <OrderPaymentEntry key={payment.uuid} payment={payment} />
          ))
        ) : (
          <div className="pb-6">No payments made to this order</div>
        )
      },
      {
        key: 'history',
        label: 'History',
        children: orderData.status_history
          .toReversed()
          .map((transition: CollectionStatusHistoryItem) => (
            <div
              className="flex justify-between gap-4 border-b py-6 first-of-type:pt-0 last-of-type:border-0"
              key={`${transition.created_at}-${transition.status}`}
              data-testid="order-details-history-entry"
            >
              <div className="flex flex-col justify-between gap-1">
                <div className="text-xs">Status</div>
                <OrderBadge state={transition.status!} />
              </div>
              <div className="flex flex-col justify-end">
                <FormattedDateTime value={transition.created_at!} />
              </div>
              <div className="flex flex-col justify-end">
                <FormattedDateTime value={transition.created_at!} />
              </div>
            </div>
          ))
      }
    );
    return sections.map((section) => ({
      ...section,
      ['data-testid']: section.key
    }));
  }, [
    orderData,
    isSuccess,
    isFeatureAccessLoading,
    hasOrderSummary,
    orderRefunded,
    hasReasonCodes,
    organisation.name,
    isSandboxTestCasesEnabled,
    onTestCaseSuccess
  ]);

  const formValues = watch();
  const canProceedToTransfer = useMemo(
    () => Object.values(formValues).every(Boolean),
    [formValues]
  );

  if (!hasValue(organisationId)) {
    return null;
  }

  return (
    <>
      <Modal
        destroyOnHidden
        open={isCancelDialogOpen}
        title={TRANSLATIONS.CANCEL_ORDER}
        footer={null}
        onCancel={() => setIsCancelDialogOpen(false)}
      >
        <div className="flex flex-col gap-4">
          {TRANSLATIONS.ARE_YOU_SURE_CANCEL}
          <div className="grid w-full grid-cols-2 gap-4">
            <SecondaryButton onClick={() => setIsCancelDialogOpen(false)}>
              Keep it
            </SecondaryButton>
            <PrimaryButton
              danger
              data-testid="cancel-order-confirm-button"
              onClick={() => cancelOrder({ organisationId, uuid: orderId })}
            >
              Cancel order
            </PrimaryButton>
          </div>
        </div>
      </Modal>
      <Modal
        destroyOnHidden
        open={isPartialRefundDialogOpen}
        title={TRANSLATIONS.PARTIAL_REFUND}
        style={{ top: 160 }}
        onCancel={() => closeRefundDialogs()}
        footer={() => (
          <PrimaryButton
            className="w-full"
            form="partialRefundForm"
            data-testid={COLLECTIONS_PARTIAL_REFUND_SUBMIT_BUTTON_TEST_ID}
            key="submit"
            htmlType="submit"
            disabled={isLoading || !canProceedToTransfer}
          >
            Perform partial refund
          </PrimaryButton>
        )}
      >
        <form
          onChange={() => clearErrors()}
          className="mt-3 flex flex-col gap-3"
          onSubmit={handleSubmit(onRefundSubmit)}
          id="partialRefundForm"
        >
          <LabelledInput label="Amount" errorMessage={errors.amount?.message}>
            <AmountInput
              status={errors?.amount ? 'error' : undefined}
              currency={orderData?.currency_code}
              onChange={(amount) => setValue('amount', amount)}
              data-testid={COLLECTIONS_PARTIAL_REFUND_AMOUNT_INPUT_TEST_ID}
              placeholder="1,000.00"
            />
          </LabelledInput>
        </form>
      </Modal>
      <Modal
        open={isFullRefundDialogOpen}
        title={TRANSLATIONS.FULL_REFUND}
        onOk={() => {
          if (!isSuccess) return;

          const amount = orderData?.amount;

          if (amount) {
            onRefundSubmit({ amount });
          }
        }}
        okText="Yes"
        okButtonProps={{ ['data-testid']: 'full-refund-confirm-button' }}
        onCancel={() => closeRefundDialogs()}
      >
        {TRANSLATIONS.ARE_YOU_SURE_REFUND}
      </Modal>

      <div
        style={{ flex: 0.25, borderRadius: '.75rem' }}
        data-testid={COLLECTION_ORDERS_DETAILS}
        className="cool-shadow overflow-y-auto"
      >
        <div className="flex justify-between rounded-t-xl bg-white px-6 py-3.5 text-xl">
          Details
          <TertiaryButton icon={<CloseOutlined />} onClick={onClose} />
        </div>
        <div className="overflow-auto bg-white">
          {isLoading && <Skeleton className="p-6" />}
          {isSuccess && (
            <Collapse
              className="antd-details-collapse bg-white"
              expandIconPosition="end"
              accordion
              bordered={false}
              defaultActiveKey={activeSection}
              key={activeSection}
              items={collapseItems}
            />
          )}
          {isError && (
            <div className="flex h-full items-center justify-center">
              {TRANSLATIONS.SOMETHING_WENT_WRONG}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
